import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import Warning from '@mui/icons-material/Warning';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PersonAdd from '@mui/icons-material/PersonAdd';
import { tenantConfigurationService } from '../../services/tenantConfigurationService';

export const DemoCleanupPanel: React.FC = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    callsDeleted: number;
    blobsDeleted: number;
    error?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Demo user seeding state
  const [seedUserId, setSeedUserId] = useState('');
  const [seedDisplayName, setSeedDisplayName] = useState('CallScore Demo');
  const [seedEmail, setSeedEmail] = useState('demo@goviq.us');
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);

  const handlePurge = async () => {
    try {
      setExecuting(true);
      setError(null);
      setResult(null);
      setConfirmOpen(false);
      const data = await tenantConfigurationService.purgeDemoTenantData();
      setResult(data);
    } catch {
      setError('Failed to purge demo tenant data. Check server logs.');
    } finally {
      setExecuting(false);
    }
  };

  const handleSeedUser = async () => {
    try {
      setSeeding(true);
      setSeedError(null);
      setSeedResult(null);
      const data = await tenantConfigurationService.seedDemoUser(seedUserId.trim(), seedDisplayName.trim(), seedEmail.trim());
      setSeedResult(data.message);
      setSeedUserId('');
    } catch {
      setSeedError('Failed to seed demo user. Verify the OID is correct and check server logs.');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Purge all call records and audio files from the Demo tenant.
        This only affects the &quot;demo&quot; tenant — no other tenant data is touched.
        Audit logs are preserved.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && result.success && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />} onClose={() => setResult(null)}>
          Cleanup complete: {result.callsDeleted} calls deleted, {result.blobsDeleted} audio files removed.
        </Alert>
      )}

      {result && !result.success && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setResult(null)}>
          Cleanup failed: {result.error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <DeleteSweep color="error" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Purge Demo Tenant Data
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Deletes all calls from the demo database and all audio/transcript blobs from demo storage.
                This action cannot be undone.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="error"
              startIcon={executing ? <CircularProgress size={16} color="inherit" /> : <DeleteSweep />}
              onClick={() => setConfirmOpen(true)}
              disabled={executing}
              sx={{ ml: 2, minWidth: 120 }}
            >
              {executing ? 'Purging...' : 'Purge Data'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Seed Demo User */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Link the demo guest user to the demo tenant. Paste the Entra ID Object ID from Azure Portal
        → Entra ID → Users → demo@goviq.us → Overview.
      </Typography>

      {seedError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSeedError(null)}>
          {seedError}
        </Alert>
      )}

      {seedResult && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />} onClose={() => setSeedResult(null)}>
          {seedResult}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PersonAdd color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Seed Demo Guest User
            </Typography>
          </Box>
          <TextField
            label="Entra ID Object ID (OID)"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            fullWidth
            size="small"
            value={seedUserId}
            onChange={e => setSeedUserId(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Display Name"
            fullWidth
            size="small"
            value={seedDisplayName}
            onChange={e => setSeedDisplayName(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Email"
            fullWidth
            size="small"
            value={seedEmail}
            onChange={e => setSeedEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={seeding ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
            onClick={handleSeedUser}
            disabled={seeding || !seedUserId.trim()}
          >
            {seeding ? 'Seeding...' : 'Seed Demo User'}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Confirm Demo Data Purge
        </DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete all call records and audio files from the Demo tenant.
            Audit logs will be preserved. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handlePurge}>
            Yes, Purge All Demo Data
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
